import CardTemplate from "components/card/card-template/CardTemplate";
import { BenefitCard } from "components/benefit-card/BenefitCard";
import { useOffer } from "hooks/useOffer";
import { useMLTAQuoteResults } from "hooks/useMLTAQuoteResults";

/**
 * MLTA Quote Results Page
 * Displays benefit plan options (e.g., Deluxe/Economy, Superior/Premier) for user selection
 * Uses the MLTA benefits data stored in Redux from the quote form submission
 */
const MLTAQuoteResultsPage = () => {
    // ========== Hooks ==========
    const { offerData } = useOffer();
    const {
        primaryPlan,
        secondaryPlan,
        hasValidBenefits,
        selectedTier,
        includeFamilyCoverage,
        setIncludeFamilyCoverage,
        familyPriceDelta,
        monthlyPrice,
        handlePlanSelect,
        handleClaimReward,
    } = useMLTAQuoteResults();

    // ========== Render ==========
    if (!hasValidBenefits) {
        return null;
    }

    let benefitHeading = "Select your benefits plan";
    if (offerData?.firstName) {
        const optionSuffix = primaryPlan && secondaryPlan ? "s" : "";
        benefitHeading = `${offerData.firstName}'s benefit option${optionSuffix}`;
    }

    const title = (
        <div className="flex flex-col gap-2 min-h-[76px] items-center justify-center">
            <p className="text-base leading-5 tracking-sm text-base-content pr-2 font-medium text-center">
                Member Loyalty<sup>®</sup>
            </p>
            <p className="text-2xl leading-5 tracking-sm text-secondary-content pr-2 font-semibold text-center">
                Accident Insurance
            </p>
        </div>
    );

    return (
        <>
            <title>Quote Results | AAA Life</title>
            <CardTemplate title={title} px="px-4 tablet:px-8">
                <div className="flex flex-col gap-4">
                    <p className="font-sans font-bold text-lg leading-7 text-base-content text-center">{benefitHeading}</p>

                    <div className="flex flex-row gap-1 justify-center">
                        {primaryPlan && (
                            <div className="flex-1">
                                <BenefitCard
                                    plan={primaryPlan}
                                    isSelected={selectedTier === primaryPlan.id}
                                    onChange={() => handlePlanSelect(primaryPlan.id)}
                                />
                            </div>
                        )}
                        {secondaryPlan && (
                            <div className="flex-1">
                                <BenefitCard
                                    plan={secondaryPlan}
                                    isSelected={selectedTier === secondaryPlan.id}
                                    onChange={() => handlePlanSelect(secondaryPlan.id)}
                                />
                            </div>
                        )}
                    </div>

                    {/* Family Coverage Checkbox */}
                    <form
                        data-testid="family-coverage-form"
                        className="flex flex-col gap-1 mt-2"
                        onSubmit={(e) => e.preventDefault()}
                    >
                        <label htmlFor="family-coverage-checkbox" className="flex items-center gap-2 cursor-pointer">
                            <input
                                id="family-coverage-checkbox"
                                name="familyCoverage"
                                type="checkbox"
                                checked={includeFamilyCoverage}
                                onChange={(e) => setIncludeFamilyCoverage(e.target.checked)}
                                className="checkbox checkbox-primary"
                                data-testid="family-coverage-checkbox"
                            />
                            <span className="font-sans font-bold text-sm leading-5 align-middle">
                                Include coverage for my family (+${familyPriceDelta.toFixed(2)})
                            </span>
                        </label>
                        <p className="font-sans font-medium text-sm leading-5 align-middle ml-8">
                            Your spouse
                            <a href="#disclosures" className="text-primary hover:underline">
                                <sup>2</sup>
                            </a>{" "}
                            is covered at 60% of the primary insured's benefits; children
                            <a href="#disclosures" className="text-primary hover:underline">
                                <sup>3</sup>
                            </a>{" "}
                            are covered at 20%.
                        </p>
                    </form>

                    {/* Coverage Price and CTA */}
                    <div className="flex items-center justify-between bg-neutral rounded-lg p-4 tablet:p-6 gap-2 tablet:gap-4">
                        <div className="flex flex-col min-w-0">
                            <span className="font-sans font-medium text-sm tablet:text-base leading-6 whitespace-nowrap">
                                {includeFamilyCoverage ? "Family Coverage" : "Individual Coverage"}
                            </span>
                            <span className="font-sans font-semibold text-xl leading-7">${monthlyPrice.toFixed(2)}/month</span>
                        </div>
                        <button
                            type="button"
                            onClick={handleClaimReward}
                            className="btn btn-primary font-sans font-semibold text-sm leading-5 text-center align-middle h-[36px] min-h-0 px-3 tablet:px-4 flex-shrink-0"
                        >
                            Claim My Reward
                        </button>
                    </div>
                </div>
            </CardTemplate>
        </>
    );
};

export default MLTAQuoteResultsPage;
